import { Component, EventEmitter, Input, Output } from "@angular/core";
import { NavController, NavParams, ViewController } from "ionic-angular";
import {
  Order,
  OrderComponent,
  OrderVisitFrequency,
} from "models/order-management/models";
import * as moment from "moment-timezone";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

export type DropdownType = "visits" | "timeInterval" | "duration";

interface Warning {
  id: string;
  message: string;
  acknowledged: boolean;
}

@Component({
  selector: "visit-frequency-description",
  templateUrl: "visit-frequency-description.html",
})
export class VisitFrequencyDescription implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() get orderCreateRequest(): Order {
    return this._orderCreateRequest;
  }
  @Input() get orderFormState(): OrderFormState {
    return this._orderFormState;
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  description: string;
  warnings: Warning[] = [];

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService
  ) {}
  private _orderCreateRequest: Order;
  private _orderFormState: OrderFormState;

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  public get shouldShow() {
    return this.description;
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  set orderFormState(orderFormState: OrderFormState) {
    this._orderFormState = orderFormState;
    this.setDescription(this._orderFormState);
    this.setWarnings();
    this.checkIsValid();
  }

  set orderCreateRequest(orderCreateRequest: Order) {
    this._orderCreateRequest = orderCreateRequest;
    this.setWarnings();
    this.checkIsValid();
  }

  setDescription(orderFormState: OrderFormState) {
    this.description = this.getDescription(orderFormState);
    const updateObject: OrderCreateRequestUpdate = { updates: {} };
    updateObject.updates["nameDescription"] = this.description;
    this.orderCreateRequestEvent.emit(updateObject);
  }

  getDescription(orderFormState: OrderFormState): string {
    const {
      timeInterval,
      visits,
      discipline,
      duration,
      PRNQuantity,
      declined,
      durationInterval,
      startDate,
    } = this._orderFormState;

    // Declined orders only show the discipline in the description
    if (discipline && declined) {
      return discipline;
    }

    // If not all information is available to set the full description
    if (!this.canGetDescription(orderFormState)) {
      // PRN Only orders show the discipline and PRN quantity
      if (
        PRNQuantity > 0 &&
        OrderVisitFrequency.isPRNOnlyOrder(orderFormState)
      ) {
        return `${discipline} - ${this.getPRNVisitDescription(
          PRNQuantity,
          true
        )}`;
      }
      // Not enough information to set a description
      return "";
    }

    // Convert quantities and intervals to descriptive text
    const visitsDescr = this.getVisitsDescription(visits);
    const timeIntervalDescr =
      OrderVisitFrequency.getIntervalDescription(timeInterval);
    const durationDescr = this.getDurationDescription(duration);
    const durationIntervalDescr = OrderVisitFrequency.getIntervalDescription(
      durationInterval,
      duration
    );
    const visitCountDescr = this.getVisitCountDescription(
      visits,
      timeInterval,
      duration,
      durationInterval,
      startDate
    );
    const prnVisitDescr = this.getPRNVisitDescription(PRNQuantity);
    return `${discipline} - ${visitsDescr} Every ${timeIntervalDescr} for ${durationDescr} ${durationIntervalDescr} (${visitCountDescr}) ${prnVisitDescr}`.trim();
  }

  canGetDescription(orderFormState: OrderFormState): boolean {
    const { timeInterval, visits, discipline, duration, durationInterval } =
      orderFormState;

    if (
      timeInterval &&
      visits > 0 &&
      discipline &&
      duration > 0 &&
      durationInterval
    )
      return true;
    return false;
  }

  getVisitsDescription(visits: number): string {
    return visits > 0 ? `${visits} Visit${visits > 1 ? "s" : ""}` : "";
  }

  getDurationDescription(duration: number): string {
    return `${duration}`;
  }

  /**
   *
   * @param interval
   * @returns days
   *
   * We assume that a month is 28 days for the purpose of calculating visit count.
   * This is not necessarily always perfect if it spans many months, but it's close enough.
   */
  getIntervalDays(interval: string): number {
    switch (interval) {
      case "DAY":
        return 1;
      case "WEEK":
        return 7;
      case "2_WEEKS":
        return 14;
      case "MONTH":
        throw new Error(
          "MONTH not supported, since calendar month lengths vary"
        );
      default:
        return 0;
    }
  }

  getVisitCountDescription(
    visits: number,
    timeInterval: string,
    duration: number,
    durationInterval: string,
    startDate: Date
  ): string {
    const totalVisits = this.getTotalVisits(
      visits,
      timeInterval,
      duration,
      durationInterval,
      startDate
    );
    return `${totalVisits} Visit${totalVisits > 1 ? "s" : ""}`;
  }

  getPRNVisitDescription(PRNQuantity: number, PRNOnly = false): string {
    return PRNQuantity > 0
      ? `${PRNOnly ? "" : "and "}${PRNQuantity} PRN Visit${
          PRNQuantity > 1 ? "s" : ""
        }`
      : "";
  }

  getTotalVisits(
    visits: number,
    timeInterval: string,
    duration: number,
    durationInterval: string,
    startDate: Date
  ): number {
    // Three ways to calculate total visits:
    // 1. If `timeInterval` is DAY and `durationInterval` is `WEEK` or `MONTH`, then `startDate - endDate` in days * visits.
    // 2. If `timeInterval` is MONTH and `durationInterval` is `MONTH`, then just visits * duration. Day count not needed.
    // 3. Otherwise, (days in time interval) divided by (days in duration interval) * visits.
    if (timeInterval == "DAY" && ["WEEK", "MONTH"].includes(durationInterval)) {
      const endDate = OrderVisitFrequency.calculateDiscontinueDate(
        startDate,
        durationInterval,
        duration
      );
      const mStartDate = moment(startDate);
      const mEndDate = moment(endDate);
      // Add 1 because startDate is start of day and endDate is end of day. When doing diff, the "end of day" does not count as a full day.
      const days = mEndDate.diff(mStartDate, "days") + 1;
      return days * visits;
    } else if (timeInterval == "MONTH" && durationInterval == "MONTH") {
      return visits * duration;
    } else {
      return (
        (visits / this.getIntervalDays(timeInterval)) *
        this.getIntervalDays(durationInterval) *
        duration
      );
    }
  }

  checkIsValid() {
    if (
      this.description &&
      this.warnings.every((warning) => warning.acknowledged)
    ) {
      this.isValidEvent.emit(true);
    } else {
      this.isValidEvent.emit(false);
    }
  }

  setWarnings() {
    const timeInterval = this._orderFormState?.timeInterval;
    const durationInterval = this._orderFormState?.durationInterval;
    const startDate = this._orderFormState?.startDate;
    const discontinueDate = moment(
      this._orderCreateRequest?.discontinueDate
    ).toDate();
    if (
      [startDate, discontinueDate].some((date) => typeof date === "undefined")
    ) {
      return;
    }
    this.checkAndAddMedicareWeekWarning(
      timeInterval,
      durationInterval,
      startDate,
      discontinueDate
    );
    this.checkAndAddBenefitPeriodWarning(
      discontinueDate,
      this._orderFormState.benefitPeriodCertificationEndDate
    );
  }

  checkAndAddMedicareWeekWarning(
    timeInterval: string,
    durationInterval: string,
    startDate: Date,
    endDate: Date
  ) {
    const warningId = "medicare-week-warning";

    // Only warn for DAY time interval and DAY duration interval
    if (!(timeInterval === "DAY" && durationInterval === "DAY")) {
      this.deleteWarning(warningId);
      return;
    }

    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    // Get next Saturday
    const saturday = startMoment.clone().day(6).endOf("day");
    const spansSaturday = saturday.isBetween(
      startMoment,
      endMoment,
      "day",
      "[)"
    );

    if (spansSaturday) {
      this.addOrUpdateWarning({
        id: warningId,
        message: this.medicareWeekWarning(saturday.toDate()),
        acknowledged: false,
      });
    } else {
      this.deleteWarning(warningId);
    }
  }

  checkAndAddBenefitPeriodWarning(
    endDate: Date,
    benefitPeriodCertificationEndDate?: Date
  ) {
    // If end date is after the benefit period certification end date, add a warning
    const endMoment = moment(endDate);

    if (!benefitPeriodCertificationEndDate) {
      return;
    }
    const benefitPeriodEndMoment = moment(benefitPeriodCertificationEndDate);

    const warning = {
      id: "benefit-period-warning",
      message: this.benefitPeriodWarning(benefitPeriodEndMoment.toDate()),
      acknowledged: false,
    };
    if (endMoment.isAfter(benefitPeriodEndMoment, "day")) {
      this.addOrUpdateWarning(warning);
    } else {
      this.deleteWarning("benefit-period-warning");
    }
  }

  addOrUpdateWarning(warning: Warning) {
    const existingWarning = this.warnings.find((w) => w.id === warning.id);
    if (existingWarning) {
      existingWarning.message = warning.message;
    } else {
      this.warnings.push(warning);
    }
  }

  deleteWarning(warningId: string) {
    this.warnings = this.warnings.filter((w) => w.id !== warningId);
  }

  medicareWeekWarning(date: Date): string {
    const dateString = moment(date).format("MM/DD/YYYY");
    return `Medicare Week warning: The entered frequency extends past Saturday, ${dateString}. Please split the visit frequency or change the Start Date.`;
  }

  benefitPeriodWarning(date: Date): string {
    const dateString = moment(date).format("dddd, MM/DD/YYYY");
    return `Benefit Period warning: The entered frequency extends past the certification end date ${dateString}. Please split the visit frequency or change the Start Date.`;
  }

  onChecked(_event: boolean) {
    this.checkIsValid();
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/visit-frequency-description/visit-frequency-description.ts